import { IInputProps, IPickerItemProps, ITag, ITagPickerProps, TagItem, TagPicker } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import { FieldErrors } from "react-hook-form";

import { getSingle } from "../../../utils/arrays/getSingleOrDefault";
import { getSortedCollection } from "../../../utils/sort/getSortedCollection";
import { TooltipHost } from "../../surfaces/tooltip/TooltipHost";
import { Label } from "../label/Label";
import { mapToTag } from "./common/mapToTag";
import { DropdownOption } from "./DropdownOption";

type MultiselectSearchDropdownProps = Pick<ITagPickerProps, "disabled"> & {
    selectedKeys: string[] | undefined;
    options: DropdownOption[];
    placeholder?: string;
    label?: string;
    tooltip?: string;
    name?: string;
    errors?: FieldErrors;
    onChange(items?: DropdownOption[]): void;
};

export const MultiselectSearchDropdown: React.FunctionComponent<MultiselectSearchDropdownProps> = (
    props: MultiselectSearchDropdownProps
) => {
    const { selectedKeys, options, placeholder, label, tooltip, disabled, onChange } = props;

    const [selectedItems, setSelectedItems] = useState<ITag[]>([]);

    useEffect(() => {
        if (!options.length) setSelectedItems([]);
        else {
            const items = selectedKeys?.map((key) => getSingle(options.filter((o) => o.key == key)));

            const tagItems: ITag[] = items ? items.map((item) => ({ name: item.text, key: item.key })) : [];

            setSelectedItems(tagItems);
        }
    }, [options, selectedKeys]);

    const filterSuggestions = (input: string, selection?: ITag[]): ITag[] => {
        const filteredOptions = filterOutSelectedOptions(selection);

        if (!input) {
            return filteredOptions;
        }

        return filteredOptions.filter((option: ITag) => isMatchingOption(input, option));
    };

    const filterOutSelectedOptions = (selection?: ITag[]): ITag[] => {
        const filteredOptions = options.filter(
            (option: DropdownOption) => !selection?.find((tag) => option.key === tag.key)
        );

        const filteredTags = filteredOptions.map(mapToTag);

        return getSortedCollection(filteredTags, "name", "asc");
    };

    const handleOnChange = (items?: ITag[]) => {
        onChange(items?.map(mapToDropdownOption));
    };

    return (
        <TooltipHost content={tooltip}>
            {label ? <Label>{label}</Label> : undefined}
            <TagPicker
                selectedItems={selectedItems}
                disabled={disabled}
                onChange={handleOnChange}
                onResolveSuggestions={filterSuggestions}
                onEmptyResolveSuggestions={filterOutSelectedOptions}
                onRenderSuggestionsItem={defaultRenderSuggestion}
                onRenderItem={defaultRenderTagItem}
                inputProps={{ placeholder, "data-testid": props.name } as IInputProps}
                pickerSuggestionsProps={{ noResultsFoundText: "No data" }}
                styles={{
                    input: { width: "20%" },
                    text: {
                        minWidth: 100,
                        border: props.name && props.errors?.[props.name] ? "2px solid red" : "1px solid"
                    }
                }}
            />
        </TooltipHost>
    );
};

export const defaultRenderSuggestion = (item: ITag): JSX.Element => (
    <div data-testid="dropdown-option" style={{ padding: "6px 12px 7px" }}>
        {item.name}
    </div>
);

export const defaultRenderTagItem = (props: IPickerItemProps<ITag>): JSX.Element => {
    return <TagItem {...props}>{props.item.name}</TagItem>;
};

const mapToDropdownOption = (tag: ITag): DropdownOption => {
    return {
        key: tag.key.toString(),
        text: tag.name
    };
};

export const isMatchingOption = (input: string, tag: ITag): boolean =>
    tag.name
        .toLowerCase()
        .split(" ")
        .some((namePart: string) => namePart.startsWith(input.toLowerCase()));
